import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { CameraIcon, PhotoIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface ImageUploaderProps {
  onImageCropped: (croppedImage: string) => void;
  mode?: 'profile' | 'logo';  // Add mode to differentiate between profile and logo
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageCropped, mode = 'profile' }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const onCropComplete = useCallback((croppedArea: CropArea, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        setShowCropper(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Set canvas size to match the cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Return as base64 string
    return canvas.toDataURL('image/jpeg');
  };

  const handleCropConfirm = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
        onImageCropped(croppedImage);
        setShowCropper(false);
      } catch (e) {
        console.error('Error cropping image:', e);
      }
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setImageSrc(null);
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      setShowCamera(true); // Show the camera UI first
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      streamRef.current = stream;
      
      // Wait for the video element to be available and then set the stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(console.error);
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Unable to access camera. Please use the upload option instead.');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg');
        setImageSrc(dataUrl);
        setShowCropper(true);
        stopCamera();
      }
    }
  };

  return (
    <div className="w-full">
      {!showCropper && !showCamera ? (
        <div className="space-y-4">
          {/* Privacy Notice - only for profile mode */}
          {mode === 'profile' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Your Privacy is Protected</p>
                  <p>Your photo is processed entirely in your browser. We never store, save, or use your images in any way. The image exists only on this page and will be deleted when you leave.</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-gray-700 font-medium">
              {mode === 'profile' ? 'Choose how to add your photo:' : 'Upload your company logo:'}
            </p>
            
            {/* Upload from device button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-white border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
            >
              <PhotoIcon className="w-6 h-6 text-gray-600" />
              <span className="text-gray-700 font-medium">
                {mode === 'profile' ? 'Upload from Device' : 'Select Logo File'}
              </span>
            </button>

            {/* Take photo button - only for profile mode */}
            {mode === 'profile' && (
              <button
                onClick={startCamera}
                className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-white border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
              >
                <CameraIcon className="w-6 h-6 text-gray-600" />
                <span className="text-gray-700 font-medium">Take a Photo</span>
              </button>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onFileChange}
            />
          </div>

          {cameraError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{cameraError}</p>
            </div>
          )}

          <p className="text-xs text-gray-500 text-center">
            Supported formats: JPG, PNG, WEBP (Max 5MB)
          </p>
        </div>
      ) : showCamera ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            Position yourself in the camera view and click capture
          </p>
          
          {/* Camera preview */}
          <div className="relative bg-black rounded-lg overflow-hidden aspect-square">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover -scale-x-100"
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(console.error);
                }
              }}
            />
            
            {/* Circular overlay guide */}
            <div className="absolute inset-0 pointer-events-none">
              <svg className="w-full h-full">
                <defs>
                  <mask id="circle-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <circle cx="50%" cy="50%" r="40%" fill="black" />
                  </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" mask="url(#circle-mask)" />
                <circle cx="50%" cy="50%" r="40%" fill="none" stroke="white" strokeWidth="2" strokeDasharray="5 5" />
              </svg>
            </div>
          </div>

          {/* Camera controls */}
          <div className="flex gap-3">
            <button
              onClick={capturePhoto}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center gap-2"
            >
              <CameraIcon className="w-5 h-5" />
              Capture Photo
            </button>
            <button
              onClick={stopCamera}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            {mode === 'profile' ? 'Position and zoom your photo for the best result' : 'Adjust your logo size and position'}
          </p>
          <div className="relative h-96 bg-gray-900 rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc!}
              crop={crop}
              zoom={zoom}
              aspect={mode === 'profile' ? 1 : undefined}  // Square for profile, free aspect for logo
              cropShape={mode === 'profile' ? "round" : "rect"}  // Circular for profile, rectangular for logo
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          <div className="space-y-2">
            <label className="block">
              <span className="text-sm text-gray-700">Zoom</span>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCropConfirm}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              Confirm Crop
            </button>
            <button
              onClick={handleCancelCrop}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;